import { z } from 'zod';
import {
  DecimalSchema,
  IdSchema,
  NonNegativeDecimalSchema,
  PaginationQuerySchema,
  VersionSchema,
  paginatedSchema,
} from './common';
import { IvaRateSchema, IvaTreatmentSchema } from './fiscal';

/* ------------------------------------------------------------------ *
 * Units of measure
 * ------------------------------------------------------------------ */

export const UnitSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  shortName: z.string(),
  /**
   * The fiscal authority's own code for this unit, used on an electronic
   * document. Text, because the formats do not agree between countries —
   * ARCA numbers its units, Chile's SII does not number them at all.
   */
  externalCode: z.string().nullable(),
  /** System units cannot be deleted, only renamed. */
  isSystem: z.boolean(),
  createdAt: z.string(),
});
export type Unit = z.infer<typeof UnitSchema>;

export const CreateUnitSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(16)
    .transform((v) => v.toLowerCase())
    .describe('Código interno de la unidad, en minúsculas (kg, m2, un).'),
  name: z.string().trim().min(1).max(64).describe('Nombre completo, por ejemplo "Kilogramo".'),
  shortName: z
    .string()
    .trim()
    .min(1)
    .max(16)
    .describe('Abreviatura que se muestra junto a la cantidad.'),
  externalCode: z
    .string()
    .trim()
    .max(16)
    .nullable()
    .optional()
    .describe(
      'Código de la unidad de medida según el organismo fiscal del país, ' +
        'para el comprobante electrónico. En Argentina, ARCA: unidad es 7, ' +
        'kilogramo 1, litro 5, metro 2, docena 9. No hay código propio para ' +
        'una caja: se usa 99 (otras unidades). En Chile el SII no publica ' +
        'códigos de unidad, así que el campo queda vacío.',
    ),
});
export type CreateUnit = z.infer<typeof CreateUnitSchema>;

export const UpdateUnitSchema = CreateUnitSchema.partial();
export type UpdateUnit = z.infer<typeof UpdateUnitSchema>;

/* ------------------------------------------------------------------ *
 * Categories (tree)
 * ------------------------------------------------------------------ */

export const CategorySchema = z.object({
  id: IdSchema,
  name: z.string(),
  parentId: IdSchema.nullable(),
  /** Materialized path of ancestor ids, used for fast subtree queries. */
  path: z.string(),
  depth: z.number().int().nonnegative(),
  productCount: z.number().int().nonnegative().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Category = z.infer<typeof CategorySchema>;

export interface CategoryTreeNode extends Category {
  children: CategoryTreeNode[];
}

export const CategoryTreeNodeSchema: z.ZodType<CategoryTreeNode> = z.lazy(() =>
  CategorySchema.extend({ children: z.array(CategoryTreeNodeSchema) }),
) as unknown as z.ZodType<CategoryTreeNode>;

export const CreateCategorySchema = z.object({
  name: z.string().trim().min(1).max(200),
  parentId: IdSchema.nullable().optional(),
});
export type CreateCategory = z.infer<typeof CreateCategorySchema>;

export const UpdateCategorySchema = CreateCategorySchema.partial();
export type UpdateCategory = z.infer<typeof UpdateCategorySchema>;

/* ------------------------------------------------------------------ *
 * Products (artículos / mercadería)
 * ------------------------------------------------------------------ */

export const PRODUCT_KINDS = ['GOODS', 'SERVICE', 'MATERIAL', 'KIT'] as const;
export const ProductKindSchema = z.enum(PRODUCT_KINDS);
export type ProductKind = z.infer<typeof ProductKindSchema>;

/** Kinds that hold physical stock and therefore produce movements. */
export const STOCKABLE_KINDS: readonly ProductKind[] = ['GOODS', 'MATERIAL', 'KIT'];

export const ProductSchema = z.object({
  id: IdSchema,
  sku: z.string(),
  barcode: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  kind: ProductKindSchema,
  /**
   * Optimistic-concurrency counter, the same one a document carries.
   *
   * Two people editing one article is quieter than two people editing one
   * document and just as wrong: the second save silently drops the first
   * person's price change. The form sends this back and the API refuses a
   * stale write.
   */
  version: VersionSchema,
  categoryId: IdSchema.nullable(),
  categoryName: z.string().nullable().optional(),
  unitId: IdSchema,
  unitShortName: z.string().optional(),
  purchasePrice: z.string(),
  salePrice: z.string(),
  /**
   * Tax rate applied to this product, as the country's own rate code.
   *
   * The picker is filled from `CountryProfile.tax.rates` rather than from a
   * list compiled into this package, because the set is the country's:
   * Argentina publishes 0 / 10.5 / 21 / 27, Chile one rate, Brazil several
   * per state. The wire form is the same string the picker offers.
   */
  ivaRate: IvaRateSchema,
  /** Whether that 0 % is exempt or outside the scope of the tax. */
  ivaTreatment: IvaTreatmentSchema,
  /** Reorder threshold; drives the low-stock report. */
  minStock: z.string(),
  weight: z.string().nullable().optional(),
  isActive: z.boolean(),
  trackStock: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Product = z.infer<typeof ProductSchema>;

export const ProductListSchema = paginatedSchema(ProductSchema);

export const CreateProductSchema = z.object({
  sku: z.string().trim().min(1).max(64).describe('Código interno; tiene que ser único.'),
  barcode: z.string().trim().max(64).nullable().optional().describe('EAN o código de barras.'),
  name: z.string().trim().min(1).max(300),
  description: z.string().trim().max(2000).nullable().optional(),
  kind: ProductKindSchema.default('GOODS').describe(
    'Artículo, servicio, material o kit. Un servicio no mueve existencias.',
  ),
  categoryId: IdSchema.nullable().optional(),
  unitId: IdSchema.describe('Unidad de medida.'),
  purchasePrice: NonNegativeDecimalSchema.default('0').describe(
    'Precio de compra, neto de IVA. Es el que alimenta el costo promedio.',
  ),
  salePrice: NonNegativeDecimalSchema.default('0').describe('Precio de venta, neto de IVA.'),
  /**
   * No default on purpose. A rate default compiled in here would be the home
   * country's, and would quietly mislabel every product of a workspace
   * elsewhere — the service writes the country's own default instead.
   */
  ivaRate: IvaRateSchema.optional().describe(
    'Alícuota de IVA. Si no se indica, se usa la del país de la empresa.',
  ),
  ivaTreatment: IvaTreatmentSchema.optional().describe(
    'Con alícuota 0, distingue una operación exenta de una no gravada; el ' +
      'comprobante dice una cosa distinta en cada caso.',
  ),
  minStock: NonNegativeDecimalSchema.default('0').describe(
    'Punto de reposición, para el aviso de stock bajo.',
  ),
  weight: DecimalSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  trackStock: z
    .boolean()
    .default(true)
    .describe(
      'Si se controla en existencias. Un servicio o un kit no descontado suele no controlarse.',
    ),
});
export type CreateProduct = z.infer<typeof CreateProductSchema>;

export const UpdateProductSchema = CreateProductSchema.partial().extend({
  /** @see UpdateDocumentSchema — omitted means "overwrite". */
  version: VersionSchema.optional(),
});
export type UpdateProduct = z.infer<typeof UpdateProductSchema>;

export const ProductQuerySchema = PaginationQuerySchema.extend({
  categoryId: IdSchema.optional(),
  kind: ProductKindSchema.optional(),
  unitId: IdSchema.optional(),
  ivaRate: IvaRateSchema.optional().describe('Filtra por alícuota de IVA.'),
  isActive: z.coerce.boolean().optional(),
  /** Only products at or below their reorder threshold. */
  lowStockOnly: z.coerce.boolean().optional(),
  warehouseId: IdSchema.optional(),
});
export type ProductQuery = z.infer<typeof ProductQuerySchema>;
export type ProductQueryInput = z.input<typeof ProductQuerySchema>;

/** Bulk price update — a common inventory-accounting operation. */
export const BulkPriceUpdateSchema = z.object({
  productIds: z.array(IdSchema).min(1).max(1000),
  /** Percentage change, e.g. 10 raises every price by 10%. */
  purchasePricePercent: z.number().min(-99).max(1000).optional(),
  salePricePercent: z.number().min(-99).max(1000).optional(),
});
export type BulkPriceUpdate = z.infer<typeof BulkPriceUpdateSchema>;

/**
 * What a duplicate-SKU conflict actually needs to say.
 *
 * "That code is taken" is true and useless: the person who typed it cannot see
 * which article holds it, whether it is the one they meant, or how to get
 * there. The identifier and the name travel in the error's `details` so the
 * form can offer to open the article that already owns the code — and, when it
 * is an article they meant to edit, take them straight to it.
 */
export const SkuConflictDetailsSchema = z.object({
  sku: z.string(),
  productId: IdSchema,
  productName: z.string(),
  /** `null` once the owner was deactivated, which is worth seeing. */
  isActive: z.boolean().nullable().optional(),
});
export type SkuConflictDetails = z.infer<typeof SkuConflictDetailsSchema>;

/** The barcode twin of {@link SkuConflictDetailsSchema}. */
export const BarcodeConflictDetailsSchema = z.object({
  barcode: z.string(),
  productId: IdSchema,
  productName: z.string(),
  isActive: z.boolean().nullable().optional(),
});
export type BarcodeConflictDetails = z.infer<typeof BarcodeConflictDetailsSchema>;
